import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { useAuth } from '../../context';
import { resetPassword } from '../../api/customers';
import { Form, FormField, SubmitButton } from '../../components/forms';
import { ActivityIndicator } from '../../components/ui';
import styles from './ForgotPasswordPage.module.css';

const validationSchema = Yup.object().shape({
  email: Yup.string().required().email().label('Email'),
});

interface ForgotPasswordFormValues {
  email: string;
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { email: savedEmail, setEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (values: ForgotPasswordFormValues) => {
    setEmail(values.email);
    setLoading(true);
    setError(null);

    const result = await resetPassword(values.email);
    setLoading(false);

    if (!result.ok) {
      setError(result.problem || 'Reset failed');
      return;
    }

    if (!result.data?.success) {
      setError('Reset failed');
      return;
    }

    alert('Reset Submitted. A reset link has been sent to your email address.');
    navigate('/');
  };

  return (
    <div className={styles.container}>
      <ActivityIndicator visible={loading} />
      <h1 className={styles.title}>Reset Password</h1>
      <p className={styles.description}>
        Enter your email address and we'll send you a link to reset your password.
      </p>
      <Form<ForgotPasswordFormValues>
        initialValues={{
          email: savedEmail || '',
        }}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <FormField
          name="email"
          icon="✉"
          placeholder="Email"
          type="email"
          autoComplete="email"
          autoCapitalize="none"
        />
        {error && <p className={styles.error}>{error}</p>}
        <SubmitButton title="Reset Password" theme="secondary" />
      </Form>
      <button className={styles.backButton} onClick={() => navigate('/')}>
        Back to Login
      </button>
    </div>
  );
}

export default ForgotPasswordPage;
